/**
 * Participant IDs: unique, random, carrying no information about the person, with an
 * optional check character that catches typing mistakes.
 *
 * - **Randomness.** Codes are drawn with `crypto.getRandomValues`, and each position is a
 *   uniform pick from the alphabet: a 32-bit word x is kept only when
 *   x < m × ⌊2³² ∕ m⌋ and used as x mod m, so no symbol is ever favoured (the same
 *   rejection rule the seeded allocation tools use). Nothing is derived from a name, a date
 *   of birth, a phone number or the time, so a code reveals nothing about its owner.
 * - **Uniqueness.** A code that has already been drawn is thrown away, so every ID in a
 *   list is different.
 * - **Check characters.** A check character is computed from the code's body and appended;
 *   a reader (or a spreadsheet, or this tool's checker) can then confirm that a typed ID is
 *   internally consistent. It detects mistakes. It is not a secret, a signature or any kind
 *   of security: anyone can compute it.
 *     - **Luhn** (mod N), the credit-card algorithm generalised to any alphabet: double every
 *       second symbol from the right, add the digits of the products, and choose the check
 *       symbol that brings the total to a multiple of N. It catches every single-symbol error
 *       and most adjacent swaps — but not 09 ↔ 90 in the decimal version.
 *     - **Damm** (decimal only), H. Michael Damm's 2004 algorithm over a weakly totally
 *       anti-symmetric quasigroup of order 10: it catches every single-digit error and every
 *       adjacent transposition.
 */

export type AlphabetId = "digits" | "base32";
export type CheckKind = "none" | "luhn" | "damm";
export type Mode = "random" | "sequential";

export const ALPHABETS: Record<AlphabetId, string> = {
  digits: "0123456789",
  /** Crockford's Base32: no I, L, O or U, so none of the look-alikes of 1, 0 and V. */
  base32: "0123456789ABCDEFGHJKMNPQRSTVWXYZ",
};

export const MAX_IDS = 5000;
export const MAX_LENGTH = 16;

/* ------------------------------------------------------------------ check characters */

/** The Luhn mod N check character for `body`, over `alphabet`. Null if a symbol is foreign. */
export function luhnCheckChar(body: string, alphabet: string): string | null {
  const n = alphabet.length;
  let factor = 2;
  let sum = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    const code = alphabet.indexOf(body[i]);
    if (code < 0) {
      return null;
    }
    const addend = factor * code;
    factor = factor === 2 ? 1 : 2;
    sum += Math.floor(addend / n) + (addend % n);
  }
  return alphabet[(n - (sum % n)) % n];
}

/** Damm's quasigroup of order 10, as given in his 2004 paper. */
const DAMM_TABLE: readonly (readonly number[])[] = [
  [0, 3, 1, 7, 5, 9, 8, 6, 4, 2],
  [7, 0, 9, 2, 1, 5, 4, 8, 6, 3],
  [4, 2, 0, 6, 8, 7, 1, 3, 5, 9],
  [1, 7, 5, 0, 9, 8, 3, 4, 2, 6],
  [6, 1, 2, 3, 0, 4, 5, 9, 7, 8],
  [3, 6, 7, 4, 2, 0, 9, 5, 8, 1],
  [5, 8, 6, 9, 7, 2, 0, 1, 3, 4],
  [8, 9, 4, 5, 3, 6, 2, 0, 1, 7],
  [9, 4, 3, 8, 6, 1, 7, 2, 0, 5],
  [2, 5, 8, 1, 4, 3, 6, 7, 9, 0],
];

const DIGITS_ONLY = /^\d+$/;

/** The Damm check digit for a string of digits; null if it holds anything else. */
export function dammCheckDigit(body: string): string | null {
  if (!DIGITS_ONLY.test(body)) {
    return null;
  }
  let interim = 0;
  for (const char of body) {
    interim = DAMM_TABLE[interim][Number(char)];
  }
  return String(interim);
}

export function checkCharFor(
  kind: CheckKind,
  body: string,
  alphabet: string
): string | null {
  if (kind === "luhn") {
    return luhnCheckChar(body, alphabet);
  }
  if (kind === "damm") {
    return dammCheckDigit(body);
  }
  return "";
}

/* ------------------------------------------------------------------ randomness */

/** Fills an array with random unsigned 32-bit words — `crypto.getRandomValues` in the app. */
export type Filler = (words: Uint32Array) => void;

const TWO_32 = 4_294_967_296;

/** A uniform integer in [0, m): rejection sampling, so there is no modulo bias. */
export function randomBelow(m: number, fill: Filler): number {
  const limit = TWO_32 - (TWO_32 % m);
  const word = new Uint32Array(1);
  while (true) {
    fill(word);
    if (word[0] < limit) {
      return word[0] % m;
    }
  }
}

/* ------------------------------------------------------------------ generating */

export interface IdOptions {
  count: number;
  mode: Mode;
  /** Symbols in the body, before the check character. */
  length: number;
  alphabet: AlphabetId;
  check: CheckKind;
  prefix: string;
  /** Put a hyphen between the prefix and the body. */
  hyphen: boolean;
  /** Split the body (and check character) into groups of this many with hyphens; 0 for none. */
  group: number;
  /** For sequential IDs: the first number. */
  start: number;
}

export type IdProblem =
  | "count"
  | "length"
  | "too-many"
  | "damm-needs-digits"
  | "start"
  | "sequential-overflow";

export type IdResult =
  | { ok: true; ids: string[] }
  | { ok: false; problem: IdProblem };

/** How many different bodies the options allow: alphabet size ^ length. */
export function spaceSize(options: IdOptions): number {
  return ALPHABETS[options.alphabet].length ** options.length;
}

function grouped(text: string, size: number): string {
  if (size <= 0 || text.length <= size) {
    return text;
  }
  const parts: string[] = [];
  for (let i = 0; i < text.length; i += size) {
    parts.push(text.slice(i, i + size));
  }
  return parts.join("-");
}

export function formatId(body: string, options: IdOptions): string {
  const alphabet = ALPHABETS[options.alphabet];
  const check = checkCharFor(options.check, body, alphabet) ?? "";
  const separator = options.hyphen && options.prefix !== "" ? "-" : "";
  return `${options.prefix}${separator}${grouped(`${body}${check}`, options.group)}`;
}

function randomBody(options: IdOptions, fill: Filler): string {
  const alphabet = ALPHABETS[options.alphabet];
  return Array.from(
    { length: options.length },
    () => alphabet[randomBelow(alphabet.length, fill)]
  ).join("");
}

function problemWith(options: IdOptions): IdProblem | null {
  if (
    !Number.isInteger(options.count) ||
    options.count < 1 ||
    options.count > MAX_IDS
  ) {
    return "count";
  }
  if (
    !Number.isInteger(options.length) ||
    options.length < 1 ||
    options.length > MAX_LENGTH
  ) {
    return "length";
  }
  if (options.check === "damm" && options.alphabet !== "digits") {
    return "damm-needs-digits";
  }
  if (options.mode === "random" && options.count > spaceSize(options)) {
    return "too-many";
  }
  if (options.mode === "sequential") {
    if (!Number.isInteger(options.start) || options.start < 0) {
      return "start";
    }
    if (
      options.alphabet !== "digits" ||
      options.start + options.count - 1 >= 10 ** options.length
    ) {
      return "sequential-overflow";
    }
  }
  return null;
}

export function generateIds(options: IdOptions, fill: Filler): IdResult {
  const problem = problemWith(options);
  if (problem) {
    return { ok: false, problem };
  }
  const ids: string[] = [];
  if (options.mode === "sequential") {
    for (let i = 0; i < options.count; i++) {
      const body = String(options.start + i).padStart(options.length, "0");
      ids.push(formatId(body, options));
    }
    return { ok: true, ids };
  }
  const seen = new Set<string>();
  while (ids.length < options.count) {
    const body = randomBody(options, fill);
    if (!seen.has(body)) {
      seen.add(body);
      ids.push(formatId(body, options));
    }
  }
  return { ok: true, ids };
}

/* ------------------------------------------------------------------ checking */

export type Verdict = "valid" | "invalid" | "unchecked" | "foreign";

const SEPARATORS = /[\s-]/g;
const NEEDS_QUOTES = /[",\r\n]/;

/**
 * Is a typed ID internally consistent under the options? "unchecked" when the options carry
 * no check character; "foreign" when it does not start with the prefix or holds symbols
 * outside the alphabet.
 */
export function validateId(raw: string, options: IdOptions): Verdict {
  if (options.check === "none") {
    return "unchecked";
  }
  const alphabet = ALPHABETS[options.alphabet];
  const upper = raw.trim().toUpperCase();
  const prefix = options.prefix.toUpperCase();
  if (!upper.startsWith(prefix)) {
    return "foreign";
  }
  const rest = upper.slice(prefix.length).replace(SEPARATORS, "");
  if (rest.length < 2 || [...rest].some((char) => !alphabet.includes(char))) {
    return "foreign";
  }
  const body = rest.slice(0, -1);
  return checkCharFor(options.check, body, alphabet) === rest.slice(-1)
    ? "valid"
    : "invalid";
}

/** The file for a participant key: the IDs and any blank columns to fill in by hand. */
export function idsToCsv(
  ids: readonly string[],
  header: string,
  extraColumns: readonly string[]
): string {
  const quote = (cell: string) =>
    NEEDS_QUOTES.test(cell) ? `"${cell.replaceAll('"', '""')}"` : cell;
  const rows = [
    [header, ...extraColumns],
    ...ids.map((id) => [id, ...extraColumns.map(() => "")]),
  ];
  return `${rows.map((row) => row.map(quote).join(",")).join("\r\n")}\r\n`;
}
